import { FC, useEffect, useState, useCallback } from 'react';
import useSWR from 'swr';
import { displaySuccessNotification, displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import ToolInformationSections from './components/ToolInformationSections/ToolInformationSections';
import ActionButtons from './components/ActionButtons/ActionButtons';
import { NudosStaticAlertInComponent } from 'components/NudosComponents';
import { LOCATION_PLACE_TYPE } from 'types/services';
import { mapFieldsToState, mapFieldsToFieldsReq, getToolInformationAlertMessages } from './utils/toolInspection.utils';
import {
  ISetToolInspectionReq,
  IInspectionCategory,
  IToolInspection,
  IToolInspectionImageReq
} from 'types/suscription/toolInspection.types';
import { IInitialFieldConfig, TToolInspectionParsedValue } from 'constants/toolInspection.constants';
import { getToolInformationTab, setToolInspection } from 'services/suscription/tool-inspection.service';
import { useOpenToolInspectionReport } from 'hooks/useOpenToolInspectionReport';
import ToolInfoTabSkeleton from './components/ToolInfoTabSkeleton/ToolInfoTabSkeleton';

import './ToolInformationTab.scss';

interface ToolInformationTabProps {
  productId: number;
}

/**
 * Pestaña de información de herramienta (inspección): carga datos, formulario y acciones.
 */
const ToolInformationTab: FC<ToolInformationTabProps> = ({ productId }) => {
  const [formFields, setFormFields] = useState<Record<number, IInitialFieldConfig> | null>(null);
  const [formImages, setFormImages] = useState<IToolInspectionImageReq[]>([]);
  const [initialDataSnapshot, setInitialDataSnapshot] = useState<string>('');
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [isEditingForm, setIsEditingForm] = useState<boolean>(false);
  const [isUpdatingInspection, setIsUpdatingInspection] = useState<boolean>(false);

  const { openFile: openInspectionReport, isGeneratingUrl: isGeneratingInspectionReportUrl } =
    useOpenToolInspectionReport();

  const {
    data: toolInformation,
    isLoading: isFetchingToolInformation,
    mutate
  } = useSWR(`tool-information-${productId}`, () => getToolInformationTab(productId));

  const { locationPlace = null, lastInspection = null, category = null } = toolInformation || {};
  const { updateMessage, ubicationMessage } = getToolInformationAlertMessages(locationPlace);
  const isWarehouseLocation = locationPlace === LOCATION_PLACE_TYPE.NUDOS;

  const setFormInitialValues = (lastInspection: IToolInspection | null, category: IInspectionCategory | null) => {
    if (!lastInspection || !category) return;

    const { fields: fieldsData = [], images: imagesData = [] } = lastInspection;
    const fields = mapFieldsToState(fieldsData, category?.name);

    setFormFields(fields);
    setFormImages(imagesData);
    setInitialDataSnapshot(JSON.stringify({ fields, images: imagesData }));
  };

  const onFieldChange = (id: number, value: TToolInspectionParsedValue) => {
    if (!formFields) return;

    setFormFields(prev => ({ ...prev!, [id]: { ...prev![id], value } }));
  };

  const handleCancelChanges = () => {
    setFormInitialValues(lastInspection, category);
    setHasFormChanges(false);
    setIsEditingForm(false);
  };

  const handleClickDownloadInspection = () => {
    if (!productId || isGeneratingInspectionReportUrl) return;

    openInspectionReport(productId.toString());
  };

  const handleEditForm = () => setIsEditingForm(true);

  const handleSaveChanges = async () => {
    if (!productId || !formFields) return;

    setIsUpdatingInspection(true);
    try {
      const setInspectionReq: ISetToolInspectionReq = {
        productId,
        fields: mapFieldsToFieldsReq(formFields),
        images: formImages
      };

      await setToolInspection(setInspectionReq);
      await mutate();

      displaySuccessNotification({
        customJSXMessage: <>Información de la herramienta actualizada exitosamente</>
      });
      setHasFormChanges(false);
      setIsEditingForm(false);
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar la información de la herramienta</>
      });
    } finally {
      setIsUpdatingInspection(false);
    }
  };

  const compareFormFields = useCallback(() => {
    if (!initialDataSnapshot || !formFields) return false;

    const currentSnapshot = JSON.stringify({
      fields: formFields,
      images: formImages
    });

    return currentSnapshot !== initialDataSnapshot;
  }, [formFields, formImages, initialDataSnapshot]);

  useEffect(() => {
    setFormInitialValues(lastInspection, category);
  }, [lastInspection, category]);

  useEffect(() => {
    setHasFormChanges(compareFormFields());
  }, [formFields, formImages, initialDataSnapshot, compareFormFields]);

  if (isFetchingToolInformation) {
    return <ToolInfoTabSkeleton />;
  }

  if (!toolInformation) {
    return (
      <div className="toolInfoTab">
        <div>No se pudo cargar la información de la herramienta</div>
      </div>
    );
  }

  return (
    <div className="toolInfoTab">
      <div className="toolInfoTabHeader">
        <NudosStaticAlertInComponent
          customClassname="toolInfoAlertMessage customNudosStaticAlertInComponentContainer"
          type="information"
          alertOptions={{ title: <span>{updateMessage}</span> }}
        />
        <p className="toolInfoTabMessage">{ubicationMessage}</p>
      </div>

      <ToolInformationSections
        productId={productId}
        category={category}
        formFields={formFields}
        formImages={formImages}
        disabled={!isWarehouseLocation || !isEditingForm || isGeneratingInspectionReportUrl}
        onFieldChange={onFieldChange}
        setFormImages={setFormImages}
      />

      <ActionButtons
        productId={productId}
        hasLastInspection={!!lastInspection}
        isWarehouseLocation={isWarehouseLocation}
        isEditingForm={isEditingForm}
        isUpdatingInspection={isUpdatingInspection}
        hasFormChanges={hasFormChanges}
        isGeneratingInspectionReportUrl={isGeneratingInspectionReportUrl}
        handleClickDownloadInspection={handleClickDownloadInspection}
        onEditForm={handleEditForm}
        onCancelChanges={handleCancelChanges}
        onSaveChanges={handleSaveChanges}
      />
    </div>
  );
};

export default ToolInformationTab;
